import type {TFunction} from 'i18next';
import {AutomationNode, NodeType} from 'crqlar-react-common/dist/models/automation/automation';

import {stepName} from './nodeCategories';

/**
 * Recipients for Send Email and Send Push. The email executor merges `profileId` (ids, or a variable
 * that may resolve to a list) and `email` (addresses or a variable), deduplicated by address; Push reads
 * only `profileId`. The variable's step label is the display snapshot — no separate name key.
 */

/** "Someone else": hand-typed values, and any stored config no option matches (shown verbatim, never dropped). */
export const OTHER_RECIPIENT = 'other';

export interface RecipientOption {
    /** Built from what the option writes, so two steps writing the same config collapse into one entry. */
    value: string;
    /** Sentence-initial; `lowerFirstLetter` makes the summary form. */
    label: string;
    profileId?: string;
    email?: string;
}

export const recipientStepName = stepName;

const variable = (step: string, field: string): string => `{{"${step}".${field}}}`;

/** `{{"Step".field}}`, whole-string; the field may be a dotted path. */
const VARIABLE_PATTERN = /^{{"([^"]+)"\.([a-zA-Z_][a-zA-Z0-9_.]*)}}$/;

const readVariable = (value: string): {step: string; field: string} | null => {
    const match = VARIABLE_PATTERN.exec(value.trim());
    return match ? {step: match[1], field: match[2]} : null;
};

/** Comma-separated, blanks dropped — how the executor reads both raw keys. */
const readList = (value?: string): string[] =>
    (value ?? '').split(',').map(entry => entry.trim()).filter(Boolean);

/** Callers pass guaranteedUpstreamNodes (nearest first); steps that carry no person add nothing. */
export const buildRecipientOptions = (nodes: AutomationNode[], t: TFunction): RecipientOption[] => {
    const options: RecipientOption[] = [];
    const seen = new Set<string>();

    const add = (option: RecipientOption) => {
        if (seen.has(option.value)) return;
        seen.add(option.value);
        options.push(option);
    };

    const byProfile = (step: string, field: string, label: string) => add({
        value: `p:${variable(step, field)}`,
        label,
        profileId: variable(step, field)
    });

    const byEmail = (step: string, label: string) => add({
        value: `e:${variable(step, 'email')}`,
        label,
        email: variable(step, 'email')
    });

    nodes.forEach(node => {
        const step = stepName(node);

        switch (node.type) {
            case NodeType.AUDIENCE:
                // A fan-out run is one guest, so the step's own `id` is that guest's profile.
                byProfile(step, 'id', t('The guest of ‘{{step}}’', {step}));
                break;
            case NodeType.TEAM_MEMBERS:
                byEmail(step, t('The team member of ‘{{step}}’', {step}));
                break;
            case NodeType.RESERVATION_CREATED:
            case NodeType.RESERVATION_STATUS_CHANGED:
                byProfile(step, 'primaryProfileId', t('The primary guest of ‘{{step}}’', {step}));
                // `profileIds` resolves to a list, which the executor mails one by one.
                byProfile(step, 'profileIds', t('Everyone on the reservation ‘{{step}}’', {step}));
                break;
            case NodeType.SURVEY_SUBMITTED:
            case NodeType.SURVEY_REMINDER_DUE:
                byProfile(step, 'profileId', t('The guest of ‘{{step}}’', {step}));
                break;
            // The only recipient guaranteed to match the invitation link the mail carries.
            case NodeType.SURVEY_INVITE:
                byProfile(step, 'profileId', t('The guest invited by ‘{{step}}’', {step}));
                break;
            case NodeType.FORM_SUBMITTED:
                byEmail(step, t('The person who submitted ‘{{step}}’', {step}));
                break;
            default:
                break;
        }
    });

    return options;
};

/** Push can address guest profiles only — staff ids and form email addresses are not profiles. */
export const buildProfileRecipientOptions = (nodes: AutomationNode[], t: TFunction): RecipientOption[] =>
    buildRecipientOptions(nodes, t).filter(option => !!option.profileId);

/** Exact string match on both keys; anything else is OTHER_RECIPIENT. */
export const matchRecipientOption = (
    config: Record<string, any> | null | undefined,
    options: RecipientOption[]
): string => {
    const profileId = (config?.profileId ?? '').trim();
    const email = (config?.email ?? '').trim();

    const match = options.find(option =>
        (option.profileId ?? '') === profileId && (option.email ?? '') === email);

    return match?.value ?? OTHER_RECIPIENT;
};

/** Always writes both keys — a leftover key would be merged in by the executor as a second recipient. */
export const applyRecipientOption = (
    config: Record<string, any>,
    option: RecipientOption | null
): Record<string, any> => ({
    ...config,
    profileId: option?.profileId,
    email: option?.email
});

export const formatProfileRecipient = (value: string | undefined, t: TFunction): string => {
    if (!value?.trim()) return '';

    const parsed = readVariable(value);

    if (parsed) {
        switch (parsed.field) {
            case 'id':
            case 'profileId':
                return t('the guest of ‘{{step}}’', {step: parsed.step});
            case 'primaryProfileId':
                return t('the primary guest of ‘{{step}}’', {step: parsed.step});
            case 'profileIds':
                return t('everyone on the reservation ‘{{step}}’', {step: parsed.step});
            default:
                return t('the guest from ‘{{step}}’', {step: parsed.step});
        }
    }

    const ids = readList(value);
    return ids.length === 1 ? t('1 profile') : t('{{count}} profiles', {count: ids.length});
};

const describeEmail = (value: string, t: TFunction): string => {
    const parsed = readVariable(value);

    // Without flow context the step's kind is unknown, so don't guess "team member".
    if (parsed) return t('the email address from ‘{{step}}’', {step: parsed.step});

    const addresses = readList(value);
    if (addresses.length <= 1) return addresses[0] ?? '';

    return t('{{address}} and {{count}} more', {
        address: addresses[0],
        count: addresses.length - 1
    });
};

/** A lower-case sentence from the config alone (no flow context); empty when there is no recipient. */
export const formatRecipient = (config: Record<string, any> | null | undefined, t: TFunction): string => {
    const profileId = (config?.profileId ?? '').trim();
    const email = (config?.email ?? '').trim();

    const parts = [
        formatProfileRecipient(profileId, t),
        email ? describeEmail(email, t) : ''
    ].filter(Boolean);

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];

    return t('{{first}} and {{second}}', {first: parts[0], second: parts[1]});
};

/** Compact source labels for a card's To row; the editor keeps its fuller recipient sentences. */
export const formatRecipientPreview = (config: Record<string, any> | null | undefined, t: TFunction): string => {
    const profileId = (config?.profileId ?? '').trim();
    const email = (config?.email ?? '').trim();
    const profile = readVariable(profileId);
    const address = readVariable(email);

    const profileLabel = profile?.field === 'primaryProfileId' ? t('Primary guest')
        : profile?.field === 'profileIds' ? t('Guests') : t('Guest');
    const parts = [
        profile ? `${profileLabel} · ${profile.step}` : formatProfileRecipient(profileId, t),
        address ? `${t('Email')} · ${address.step}` : email ? describeEmail(email, t) : ''
    ].filter(Boolean);

    return parts.join('; ');
};

/** Safe because every option label opens with an article or pronoun in en, de and it. */
export const lowerFirstLetter = (value: string): string =>
    value ? value.charAt(0).toLowerCase() + value.slice(1) : value;
